import { useMemo, useState } from 'react';
import { Trigger } from '@/hooks/useTriggerStream';
import { GREENS } from './vizPalette';


interface Props {
  triggers: Trigger[];
  dark: boolean;
}

const DAYS = ['ma', 'di', 'wo', 'do', 'vr', 'za', 'zo'];

export function ActivityHeatmap({ triggers, dark }: Props) {
  const [hover, setHover] = useState<{ day: number; hour: number; count: number } | null>(null);

  const grid = useMemo(() => {
    const g: number[][] = Array.from({ length: 7 }, () => Array(24).fill(0));
    triggers.forEach((t) => {
      const d = new Date(t.created_at);
      // JS getDay: 0=Sun..6=Sat. We want Mon=0..Sun=6
      const day = (d.getDay() + 6) % 7;
      const hour = d.getHours();
      g[day][hour]++;
    });
    return g;
  }, [triggers]);

  const max = useMemo(() => {
    let m = 0;
    grid.forEach((row) => row.forEach((v) => (m = Math.max(m, v))));
    return m;
  }, [grid]);

  const colorFor = (n: number): string => {
    if (n <= 0) return dark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)';
    const t = Math.min(1, n / Math.max(1, max));
    const idx = Math.min(GREENS.length - 1, 1 + Math.floor(t * (GREENS.length - 2)));
    return GREENS[idx];
  };

  const cellW = 40;
  const cellH = 36;
  const padL = 60;
  const padT = 30;
  const W = padL + 24 * cellW + 20;
  const H = padT + 7 * cellH + 20;

  const text = dark ? 'rgba(255,255,255,0.55)' : 'rgba(0,0,0,0.55)';
  const stroke = dark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)';

  return (
    <div className="relative w-full">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto">
        {/* Hour labels (every 3) */}
        {Array.from({ length: 24 }, (_, h) => (
          <text
            key={h}
            x={padL + h * cellW + cellW / 2}
            y={padT - 10}
            textAnchor="middle"
            fontSize={9}
            fontFamily="ui-monospace, monospace"
            fill={text}
            opacity={h % 3 === 0 ? 1 : 0.3}
          >
            {String(h).padStart(2, '0')}
          </text>
        ))}
        {/* Day labels */}
        {DAYS.map((d, i) => (
          <text
            key={d}
            x={padL - 12}
            y={padT + i * cellH + cellH / 2 + 3}
            textAnchor="end"
            fontSize={10}
            fontFamily="ui-monospace, monospace"
            fill={text}
          >
            {d}
          </text>
        ))}
        {/* Cells */}
        {grid.map((row, day) =>
          row.map((count, hour) => (
            <rect
              key={`${day}-${hour}`}
              x={padL + hour * cellW + 2}
              y={padT + day * cellH + 2}
              width={cellW - 4}
              height={cellH - 4}
              rx={3}
              fill={colorFor(count)}
              stroke={stroke}
              strokeWidth={0.5}
              onMouseEnter={() => setHover({ day, hour, count })}
              onMouseLeave={() => setHover(null)}
              style={{ cursor: count ? 'pointer' : 'default' }}
            />
          ))
        )}
      </svg>
      {hover && (
        <div
          className={`pointer-events-none absolute top-2 right-2 px-3 py-2 text-xs font-mono rounded ${
            dark ? 'bg-white text-black' : 'bg-black text-white'
          }`}
        >
          {DAYS[hover.day]} · {String(hover.hour).padStart(2, '0')}:00 — {hover.count} trigger{hover.count === 1 ? '' : 's'}
        </div>
      )}
    </div>
  );
}
